import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
    CircularProgress,
    Snackbar,
    Alert,
    ThemeProvider,
    createTheme,
    Divider,
    Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { isMobile } from "react-device-detect";
import UsuarioService from "../../services/UsuarioService";

// === Types ===
type Market = {
    id: number;
    mercado: string;
    descricao: string;
    ativo: boolean;
    criado_em: string;
};

type MyTabPanelProps = {
    children?: React.ReactNode;
    value: number | string;
    index: number | string;
};

function MyTabPanel({ children, value, index }: MyTabPanelProps) {
    const show = Number(value) === Number(index);
    return (
        <div role="tabpanel" hidden={!show} id={`tabpanel-${index}`}>
            {show && children}
        </div>
    );
}

const COLORS = {
    gray: "#E8E8E8",
    purple: "#4B1196",
    lilac: "#dbcfea",
    bg: "#F3F5F7",
};

const theme = createTheme({
    palette: {
        mode: "light",
        primary: { main: COLORS.purple },
        background: { default: COLORS.bg },
    },
    shape: { borderRadius: 12 },
});

const API_BASE = "/api/mercados";

// === Row component ===
function MarketRow({
    market,
    onToggleActive,
    onDelete,
}: {
    market: Market;
    onToggleActive: (item: Market, checked: boolean) => void;
    onDelete: (item: Market) => void;
}) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: COLORS.lilac,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{
                            textTransform: "uppercase",
                            color: COLORS.purple,
                            opacity: market.ativo ? 1 : 0.6,
                        }}
                    >
                        {market.mercado}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.secondary",
                            textDecoration: market.ativo ? "none" : "line-through",
                            wordBreak: "break-word",
                        }}
                    >
                        {market.descricao}
                    </Typography>
                </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!market.ativo}
                            onChange={(e) => onToggleActive(market, e.target.checked)}
                            sx={{ color: COLORS.purple, "&.Mui-checked": { color: COLORS.purple } }}
                        />
                    }
                    label={<Typography variant="body2">Ativo</Typography>}
                />
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <IconButton aria-label="Excluir" onClick={() => onDelete(market)}>
                    <DeleteOutlineIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
}

// === Dialog: Novo Mercado ===
function NewMarketDialog({
    open,
    onClose,
    onCreate,
}: {
    open: boolean;
    onClose: () => void;
    onCreate: (payload: { mercado: string; descricao: string; ativo: boolean }) => void;
}) {
    const [mercado, setMercado] = useState("");
    const [descricao, setDescricao] = useState("");
    const [ativo, setAtivo] = useState(true);

    const canSave = mercado.trim().length > 0;

    const handleSubmit = () => {
        if (!canSave) return;
        onCreate({ mercado: mercado.trim(), descricao: descricao.trim(), ativo });
        setMercado("");
        setDescricao("");
        setAtivo(true);
    };

    useEffect(() => {
        if (!open) {
            setMercado("");
            setDescricao("");
            setAtivo(true);
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Novo mercado</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Nome do mercado"
                        value={mercado}
                        onChange={(e) => setMercado(e.target.value)}
                        autoFocus
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '7px',
                                '& fieldset': { borderRadius: '7px' },
                            },
                        }}
                    />
                    <TextField
                        label="Descrição"
                        value={descricao}
                        onChange={(e) => setDescricao(e.target.value)}
                        multiline
                        minRows={3}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '7px',
                                '& fieldset': { borderRadius: '7px' },
                            },
                        }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />}
                        label="Ativo"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!canSave}
                    sx={{
                        bgcolor: COLORS.purple,
                        "&:hover": { bgcolor: COLORS.purple },
                        borderRadius: '7px'
                    }}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// === Dialog: Confirmar exclusão ===
function ConfirmDeleteDialog({
    open,
    item,
    onCancel,
    onConfirm,
}: {
    open: boolean;
    item: Market | null;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>Excluir mercado</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Tem certeza que deseja excluir {item ? `“${item.mercado}”` : "este mercado"}?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancelar</Button>
                <Button variant="contained" color="error" onClick={onConfirm} startIcon={<DeleteOutlineIcon />}>Excluir</Button>
            </DialogActions>
        </Dialog>
    );
}

// === Page ===
export default function MarketsPage(props: { id_user: number, activeTab: any, index: number, onChange?: () => void, id_permissao?: number }) {
    const [markets, setMarkets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [q, setQ] = useState("");
    const [openNew, setOpenNew] = useState(false);
    const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: "success" | "error" | "info" }>(
        { open: false, msg: "", sev: "success" }
    );
    const [confirmState, setConfirmState] = useState<{ open: boolean; item: Market | null }>({ open: false, item: null });

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const data = await fetchMarkets();
                setMarkets(data);
            } catch (e: any) {
                setError(e?.message ?? 'Erro ao carregar');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        if (!query) return markets;
        return markets.filter(
            (m) =>
                m.mercado.toLowerCase().includes(query) ||
                (m.descricao ?? "").toLowerCase().includes(query)
        );
    }, [q, markets]);

    const handleToggleActive = async (item: Market, checked: boolean) => {
        try {
            const updated = await updateMarket(item.id, { ativo: checked });
            setMarkets((prev) => prev.map((m) => (m.id === item.id ? updated : m)));
            setSnack({ open: true, msg: `Atualizado: ${updated.mercado}`, sev: "success" });
        } catch (e: any) {
            setSnack({ open: true, msg: e?.message ?? "Erro ao atualizar", sev: "error" });
        }
    };

    const fetchMarkets = async (): Promise<Market[]> => {
        try {
            const res = await UsuarioService.GetMercadoLista();

            if (Array.isArray(res)) return res;
            if (Array.isArray(res?.data)) return res.data;
            if (res?.success && Array.isArray(res?.data)) return res.data;

            throw new Error('Resposta inesperada ao listar mercados');
        } catch (err) {
            console.error(err);
            throw err;
        }
    };

    async function createMarket(payload: Omit<Market, "id" | "criado_em">): Promise<Market> {
        const result = await UsuarioService.CreateMercado(payload);

        if (result && result.id) return result as Market;
        if (result?.data && result.data.id) return result.data as Market;

        throw new Error("Falha ao criar mercado");
    }

    async function updateMarket(id: number, patch: Partial<Market>): Promise<Market> {
        const result = await UsuarioService.UpdateMercado(id, patch);

        if (result && result.id) return result as Market;
        if (result?.data && result.data.id) return result.data as Market;

        throw new Error("Falha ao atualizar mercado");
    }

    async function deleteMarket(item: Market): Promise<void> {
        const ok = await UsuarioService.DeleteMercado(item.id);
        if (!ok) throw new Error("Falha ao excluir mercado");
    }

    const requestDelete = (item: Market) => {
        setConfirmState({ open: true, item });
    };

    const confirmDelete = async () => {
        const item = confirmState.item;
        if (!item) return;
        try {
            await deleteMarket(item);
            setMarkets((prev) => prev.filter((m) => m.id !== item.id));
            setSnack({ open: true, msg: "Excluído com sucesso", sev: "success" });
        } catch (e: any) {
            setSnack({ open: true, msg: e?.message ?? "Erro ao excluir", sev: "error" });
        } finally {
            setConfirmState({ open: false, item: null });
        }
    };

    const handleCreate = async (payload: { mercado: string; descricao: string; ativo: boolean }) => {
        try {
            const created = await createMarket(payload);
            setMarkets((prev) => [created, ...prev]);
            setOpenNew(false);
            setSnack({ open: true, msg: "Criado com sucesso", sev: "success" });
        } catch (e: any) {
            setSnack({ open: true, msg: e?.message ?? "Erro ao criar", sev: "error" });
        }
    };

    return (
        <Grid style={{ borderRadius: "6px", marginTop: `${isMobile ? "0px" : props.id_permissao != 4 ? "-30px" : "0px"}` }}>
            <MyTabPanel value={props.activeTab} index={props.index}>
                {props.id_permissao != 4
                    ?
                    <ThemeProvider theme={theme}>
                        <Box sx={{ bgcolor: '#fff', minHeight: '100vh', py: 3 }}>
                            <Container maxWidth="lg">
                                {/* Header card */}
                                <Paper
                                    elevation={0}
                                    sx={{ p: 2, mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {markets.length} Mercado(s)
                                    </Typography>
                                    <Button
                                        startIcon={<AddIcon />}
                                        variant="contained"
                                        onClick={() => setOpenNew(true)}
                                        sx={{
                                            bgcolor: COLORS.purple,
                                            "&:hover": { bgcolor: COLORS.purple },
                                            textTransform: "none",
                                            borderRadius: '7px'
                                        }}
                                    >
                                        Cadastrar
                                    </Button>
                                </Paper>

                                {/* Search */}
                                <Paper elevation={0} sx={{ p: 1.5, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar"
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '7px',
                                                '& fieldset': { borderRadius: '7px' },
                                            },
                                        }}
                                    />
                                </Paper>

                                {/* List */}
                                <Stack sx={{ pl: 2, pr: 2 }} spacing={1.5}>
                                    {loading && (
                                        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                                            <CircularProgress />
                                        </Box>
                                    )}

                                    {error && (
                                        <Alert severity="error">{error}</Alert>
                                    )}

                                    {!loading && !error && filtered.length === 0 && (
                                        <Alert severity="info">Nenhum mercado encontrado.</Alert>
                                    )}

                                    {!loading && !error && filtered.map((m) => (
                                        <MarketRow
                                            key={m.id}
                                            market={m}
                                            onToggleActive={handleToggleActive}
                                            onDelete={requestDelete}
                                        />
                                    ))}
                                </Stack>
                            </Container>

                            {/* Dialog */}
                            <NewMarketDialog open={openNew} onClose={() => setOpenNew(false)} onCreate={handleCreate} />
                            <ConfirmDeleteDialog
                                open={confirmState.open}
                                item={confirmState.item}
                                onCancel={() => setConfirmState({ open: false, item: null })}
                                onConfirm={confirmDelete}
                            />

                            {/* Snack */}
                            <Snackbar
                                open={snack.open}
                                autoHideDuration={3000}
                                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            >
                                <Alert severity={snack.sev} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
                                    {snack.msg}
                                </Alert>
                            </Snackbar>
                        </Box>
                    </ThemeProvider>
                    :
                    null}
            </MyTabPanel>
        </Grid>
    );
}
